import { Save, Trash2 } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TradeIdeasSink, TradeIdeasSinkStatus } from "@/lib/tradeideas/types";

const SINK_TYPES = ["signals_webhook", "webhook", "discord", "telegram"];

export function DeliverySinksTable({
  sinks,
  pending,
  statuses,
  onSave,
  onDelete,
}: {
  sinks: TradeIdeasSink[];
  pending: boolean;
  statuses: TradeIdeasSinkStatus[];
  onSave: (name: string, sink: TradeIdeasSink) => void;
  onDelete: (name: string) => void;
}) {
  const statusByName = new Map(statuses.map((status) => [status.name, status]));
  return (
    <section className="overflow-hidden rounded-md border border-border">
      <div className="grid grid-cols-[150px_150px_72px_1.4fr_1fr_1fr_140px] border-b border-border bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-fg">
        <span>Name</span>
        <span>Type</span>
        <span>On</span>
        <span>URL</span>
        <span>Target</span>
        <span>Secret env</span>
        <span className="text-right">Actions</span>
      </div>
      {sinks.map((sink) => (
        <DeliverySinkRow
          key={sink.name}
          sink={sink}
          status={statusByName.get(sink.name)}
          pending={pending}
          onSave={onSave}
          onDelete={onDelete}
        />
      ))}
    </section>
  );
}

function DeliverySinkRow({
  sink,
  status,
  pending,
  onSave,
  onDelete,
}: {
  sink: TradeIdeasSink;
  status: TradeIdeasSinkStatus | undefined;
  pending: boolean;
  onSave: (name: string, sink: TradeIdeasSink) => void;
  onDelete: (name: string) => void;
}) {
  const [enabled, setEnabled] = useState(sink.enabled);
  const [type, setType] = useState(sink.type);
  const [url, setUrl] = useState(sink.url ?? "");
  const [target, setTarget] = useState(sink.target ?? "");
  const [secretEnv, setSecretEnv] = useState(sink.secret_env ?? "");

  function save() {
    onSave(sink.name, {
      ...sink,
      enabled,
      type,
      url,
      target,
      secret_env: secretEnv,
    });
  }

  return (
    <div className="grid grid-cols-[150px_150px_72px_1.4fr_1fr_1fr_140px] items-center gap-2 border-b border-border/60 px-3 py-2 last:border-b-0">
      <div className="min-w-0">
        <div className="truncate font-medium">{sink.name}</div>
        <div className="mt-1 flex flex-wrap gap-1">
          <Badge variant={enabled ? "success" : "outline"}>
            {enabled ? "enabled" : "disabled"}
          </Badge>
          {status?.needs_attention ? (
            <Badge variant="danger">{status.dead > 0 ? "dead" : "queued"}</Badge>
          ) : null}
        </div>
      </div>
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SINK_TYPES.map((sinkType) => (
            <SelectItem key={sinkType} value={sinkType}>
              {sinkType}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <input
        type="checkbox"
        checked={enabled}
        onChange={(event) => setEnabled(event.currentTarget.checked)}
        className="size-4 accent-accent"
        aria-label={`Enable ${sink.name}`}
      />
      <Input value={url} onChange={(event) => setUrl(event.currentTarget.value)} />
      <Input value={target} onChange={(event) => setTarget(event.currentTarget.value)} />
      <Input
        value={secretEnv}
        onChange={(event) => setSecretEnv(event.currentTarget.value)}
      />
      <div className="flex justify-end gap-1">
        <Button
          type="button"
          size="icon"
          variant="outline"
          onClick={save}
          disabled={pending}
          title="Save sink"
        >
          <Save className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          onClick={() => onDelete(sink.name)}
          disabled={pending}
          title="Delete sink"
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
      {status ? (
        <div className="col-span-7 -mt-1 flex min-w-0 flex-wrap gap-2 pl-[calc(150px+0.5rem)] text-[11px] text-muted-fg">
          <span>sent {status.sent}</span>
          <span>pending {status.pending}</span>
          <span>retry {status.retry}</span>
          <span>dead {status.dead}</span>
          {status.attention_reason ? <span className="truncate">{status.attention_reason}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
